export type SubscriptionStatus =
  | "none"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled";

/** Stripe SKUs — map to price IDs in env. */
export type SubscriptionTier = "assistant" | "assistant_usage" | "byo_claude";

/**
 * Where chat + helpers run inference.
 * - `hosted` — Prime’s cloud model (Modal)
 * - `claude` — the broker’s own Anthropic (Claude) API key
 */
export type InferenceProvider = "hosted" | "claude";

export type TenantMcpStatus =
  | "not_provisioned"
  | "provisioning"
  | "active"
  | "needs_attention";

export type ConnectionProvider =
  | "velocity"
  | "twilio"
  | "gmail"
  | "google_drive"
  | "google_calendar"
  | "microsoft_outlook"
  | "microsoft_onedrive"
  | "hushmail"
  | "newton_link"
  | "newton_payroll"
  | "newton_product"
  | "newton_lender"
  | "newton_servicing"
  | "newton_supplier"
  | "anthropic";

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "needs_attention";

/** `email_yes_no` is legacy — helpers always auto-run; value is normalized to `none`. */
export type ApprovalPolicy = "none" | "email_yes_no";

export type ApprovalDecision = "pending" | "approved" | "denied" | "expired";

export type HelperRunStatus =
  | "scheduled"
  | "running"
  | "awaiting_approval"
  | "completed"
  | "stopped"
  | "failed";

export interface Tenant {
  id: string;
  name: string;
  stripeCustomerId: string | null;
  subscriptionStatus: SubscriptionStatus;
  subscriptionTier: SubscriptionTier;
  /** Chat + helpers use this provider when a Claude key is connected. */
  inferenceProvider: InferenceProvider;
  /** Brokerage-wide Claude model when users have no personal choice. */
  claudeDefaultModel?: string | null;
  /** Lightweight pre-send reply check (cheap Claude model). */
  answerVerificationEnabled?: boolean;
  createdAt: string;
}

export interface TenantMcp {
  tenantId: string;
  mcpBaseUrl: string;
  flyAppName: string | null;
  status: TenantMcpStatus;
  lastProvisionedAt: string | null;
}

export interface User {
  id: string;
  tenantId: string;
  googleSub: string | null;
  email: string;
  name: string;
  role: "owner" | "broker" | "member";
  /** Personal Claude model id for chat (Claude-provider tenants). */
  claudeModel?: string | null;
}

export interface Connection {
  tenantId: string;
  provider: ConnectionProvider;
  status: ConnectionStatus;
  encryptedCredentialsRef: string | null;
  lastSyncAt: string | null;
}

export interface Helper {
  id: string;
  tenantId: string;
  displayName: string;
  goalText: string;
  /** Broker-facing wording, e.g. "2" or "every 30 minutes". */
  frequencyText: string;
  /** Compiled 5-field cron (UTC), when schedule is clock-based. */
  cronExpression: string | null;
  /** Loop interval in ms — primary driver for the in-app scheduler. */
  intervalMs: number | null;
  /** Approximate runs per day (for UI). */
  timesPerDay: number;
  /** Human label from the compiler, e.g. "Every 30 minutes". */
  scheduleLabel: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  approvalPolicy: ApprovalPolicy;
  approvalWhenText: string;
  /**
   * Rolling notes this helper keeps between runs so it does not repeat
   * finished follow-ups. Broker-editable.
   */
  memoryText: string;
  active: boolean;
  createdAt: string;
  /** Broker who created the helper; runs use that broker's Google account. */
  ownerEmail?: string | null;
  /** Optional Claude model for this helper (Claude-provider tenants); null = user/tenant default. */
  claudeModel?: string | null;
  /** When set, helper was created from a loan-officer builtin template. */
  builtinTemplateKey?: string | null;
  /** Routine may send email without asking (off by default: replies are saved as drafts). */
  autoSendEmail?: boolean;
}

export interface BrokerPromptTemplate {
  id: string;
  tenantId: string;
  ownerUserId: string;
  builtinKey: string | null;
  title: string;
  body: string;
  category: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** Shared with teammates in the same workspace (tenant). */
  shared?: boolean;
  /** Owner's display name (shown on team templates). */
  ownerName?: string | null;
  /** Playbooks (loan-officer skill ids) turned on when this template is used. */
  skillIds?: string[];
}

export interface AgentTraceStep {
  phase: "plan" | "act" | "build";
  label: string;
  detail?: string;
  ok?: boolean;
}

export interface HelperRun {
  id: string;
  helperId: string;
  status: HelperRunStatus;
  summary: string;
  startedAt: string;
  finishedAt: string | null;
  approvalTokenId: string | null;
  /** Plan / tool / subagent steps from the shared agent loop (optional). */
  agentTrace?: AgentTraceStep[];
  /** Inference provider used for this run (hosted | claude). */
  provider?: string | null;
  model?: string | null;
  /** When status is failed, a short error for the run log. */
  error?: string | null;
}

export interface ApprovalRequest {
  id: string;
  helperRunId: string;
  brokerEmail: string;
  token: string;
  expiresAt: string;
  decision: ApprovalDecision;
  decidedAt: string | null;
}

export interface CreateHelperInput {
  goalText: string;
  frequencyText: string;
  approvalWhenText: string;
  memoryText?: string;
}

export interface UpdateHelperInput {
  goalText?: string;
  frequencyText?: string;
  approvalWhenText?: string;
  memoryText?: string;
  active?: boolean;
}

export interface ImprovedHelperCommand {
  goalText: string;
  /** false when Prime's AI was unavailable and a template was used. */
  aiUsed?: boolean;
  frequencyText?: string;
  approvalWhenText?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  helperName?: string;
  createdAt: string;
  /** Agent steps in plain broker words (live replies only). */
  steps?: Array<{ label: string; ok: boolean }>;
}

export interface ChatSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface VaultEncryptResult {
  ciphertext: string;
  keyVersion: number;
}

export interface VaultService {
  encrypt(plaintext: string, purpose: string): Promise<VaultEncryptResult>;
  decrypt(ciphertext: string, keyVersion: number): Promise<string>;
}

export type InferenceUsageSource =
  | "chat"
  | "inference"
  | "helper"
  | "verification"
  | "web_research"
  | "realtime";

export interface InferenceUsageSourceSlice {
  source: InferenceUsageSource;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Modeled platform cost for this slice (operators / super-admin views). */
  platformCostUsd?: number;
  /** Customer bill for this slice (platform cost × markup). */
  estimatedCostUsd: number;
}

export type InferenceUsageProviderKey = "hosted" | "claude";

export interface InferenceUsageProviderSlice {
  provider: InferenceUsageProviderKey;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  platformCostUsd: number;
  customerBillUsd: number;
}

export interface PlatformUsageTenantSlice {
  tenantId: string;
  tenantName: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  platformCostUsd: number;
  customerBillUsd: number;
}

export interface PlatformUsageUserSlice {
  userId: string;
  userEmail: string;
  userName: string;
  tenantId: string;
  tenantName: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  platformCostUsd: number;
  customerBillUsd: number;
}

/** All brokerages / users for the platform owner (super admin only). */
export interface PlatformMonthlyInferenceUsageSummary {
  month: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  platformCostUsd: number;
  customerBillUsd: number;
  gpuSeconds: number;
  billingMarkup: number;
  bySource: InferenceUsageSourceSlice[];
  byProvider: InferenceUsageProviderSlice[];
  byTenant: PlatformUsageTenantSlice[];
  byUser: PlatformUsageUserSlice[];
  stub?: boolean;
}

export interface MonthlyInferenceUsageSummary {
  month: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Customer bill at billingMarkup × platform cost (shown even for operators). */
  estimatedCostUsd: number;
  /** Modeled Modal GPU + Netlify/Fly cost before markup (GPU-on time when known). */
  platformCostUsd: number;
  /** Seconds the chat GPU would stay billed from this month's generate timestamps. */
  gpuSeconds: number;
  /** Customer multiplier (default 10). Always the rate; operators are not charged. */
  billingMarkup: number;
  /** False for platform operators — they are not charged the markup. */
  billedToCustomer: boolean;
  /** True while the user is on the unpaid free AI allowance. */
  onFreeAllowance: boolean;
  /** Free platform-spend cap in USD (default $2). */
  freeSpendCapUsd: number;
  /** Platform cost used toward the free cap this month. */
  freeSpendUsedUsd: number;
  /**
   * Which spend budget applies this month for hard stops / progress bars.
   * - free: unpaid free allowance (hard stop at freeSpendCapUsd)
   * - flat_included: monthly plan included cloud cost (price ÷ markup)
   * - metered: usage plan (Stripe meters; no hard spend stop)
   * - unlimited: reserved (not used for live AI gates)
   */
  spendCapKind: "free" | "flat_included" | "metered" | "unlimited";
  /** Active hard spend cap in USD (null when metered/unlimited). */
  spendCapUsd: number | null;
  /** Platform cost counted against the active spend cap this month. */
  spendUsedUsd: number;
  /** Flat monthly list price used for included-allowance math (CAD). */
  flatPlanPrice: number;
  /** Soft display allowance for progress bars (paid plans). */
  softTokenLimit: number;
  /**
   * True when inference must stop until the user adds a card / buys usage
   * (free or flat included allowance spent) or until next month resets.
   */
  usageLimitReached: boolean;
  bySource: InferenceUsageSourceSlice[];
  stub?: boolean;
}

/** Soft monthly token allowance used for UI progress bars on metered plans. */
export const MONTHLY_USAGE_SOFT_TOKEN_LIMIT = 1_000_000;

/**
 * Max platform AI cost (USD) an unpaid user may consume before they must
 * leave a card on file or buy more usage. Applies to every account.
 */
export const FREE_USAGE_SPEND_CAP_USD = 2;

/**
 * Flat Prime Assistant monthly list price per broker (CAD). Included cloud
 * spend is `price / INFERENCE_BILLING_MARKUP` so we keep ~10× margin, and the
 * hard monthly AI token cap (see ai-token-cap.ts) bounds Claude + Qwen use.
 */
export const ASSISTANT_FLAT_MONTHLY_PRICE = 99;

/**
 * Bring-your-own-Claude platform subscription (CAD). Brokers pay Anthropic for
 * model usage; this fee covers Prime workspace, tools, helpers, and hosting.
 */
export const ASSISTANT_BYO_CLAUDE_MONTHLY_PRICE = 40;

/**
 * Customers pay this × our modeled cloud cost (Modal GPU + Netlify / Fly).
 * Operators are not billed, but usage UI still shows this customer rate.
 */
export const INFERENCE_BILLING_MARKUP_DEFAULT = 10;

/**
 * Platform AI spend included in the flat monthly plan so margin stays
 * ≈ billing markup (default 10×): price ÷ markup → $99 / 10 = $9.90.
 */
export function flatPlanIncludedPlatformSpendUsd(
  monthlyPrice: number = ASSISTANT_FLAT_MONTHLY_PRICE,
  markup: number = INFERENCE_BILLING_MARKUP_DEFAULT,
): number {
  const price = Number.isFinite(monthlyPrice) && monthlyPrice > 0
    ? monthlyPrice
    : ASSISTANT_FLAT_MONTHLY_PRICE;
  const factor =
    Number.isFinite(markup) && markup >= 1
      ? markup
      : INFERENCE_BILLING_MARKUP_DEFAULT;
  return Math.round((price / factor) * 100) / 100;
}
